'use client'

import { useMemo } from 'react'

import { useAuthContext } from '@/hooks/use-auth-context'
import { switchSelfOrg } from '@/lib/self-auth/org-switch'

export interface SwitchableOrg {
  id: string
  slug: string
  name: string
  imageUrl: string | null
}

export interface OrgSwitcher {
  orgs: SwitchableOrg[]
  isLoading: boolean
  /** Switch the active org and hard-navigate to it. Throws on failure. */
  switchOrg: (org: SwitchableOrg) => Promise<void>
}

/**
 * Org switcher backing the sidebar switcher. The org list comes from
 * `useAuthContext().organizations`, so the app shell has one org-list source.
 * Switching activates the org (refresh) and hard-navigates via `switchSelfOrg`.
 */
export function useOrgSwitcher(): OrgSwitcher {
  const { organizations } = useAuthContext()
  const orgs = useMemo(
    () =>
      organizations.data
        .map(org => ({
          id: org.id,
          slug: org.slug,
          name: org.name ?? org.slug,
          imageUrl: org.logoUrl,
        })),
    [organizations.data],
  )

  return {
    orgs,
    isLoading: organizations.isLoading && !organizations.isLoaded,
    switchOrg: async org => {
      await switchSelfOrg({ orgId: org.id, slug: org.slug })
    },
  }
}
